import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import logoBase64 from "../../prescricoes/services/logoBase64";
import {
  normalizarCodigo,
  formatarCodigoExibicao,
} from "../services/validacaoCliente";

// ============================================
// 🔗 PÁGINA PÚBLICA DE VALIDAÇÃO DE DOCUMENTOS (B290, AI_129 · B292, AI_132)
// ============================================
// Rota PÚBLICA /validar/:codigo (sem login). A farmácia/paciente escaneia o QR do
// rodapé do documento assinado (ou recebe o link por SMS/WhatsApp — B292) e cai
// aqui. Mostra a AUTENTICIDADE (médico/CRM/data/tipo) direto; o CONTEÚDO (o PDF
// assinado) só é revelado após a 2ª conferência — o 2º fator informado pelo GET em
// `meta.fator`: os 4 últimos dígitos do celular do paciente (B292; documentos novos)
// ou a data de nascimento (B290; documentos antigos). Protege o dado de saúde.
// É COMPLEMENTAR ao ICP-Brasil — a validação legal segue em validar.iti.gov.br.
// Toda a leitura passa pelo endpoint público /api/validar (admin SDK); o Firestore
// nunca é lido sem login.

const COR_AZUL = "#1e40af";
const COR_VERDE = "#2d6a2e";

const ROTULO_TIPO: Record<string, string> = {
  receituario: "Receituário",
  "receituario-controle": "Receituário de Controle Especial",
  "receituario-controle-eletronica": "Receita de Controle Especial Eletrônica",
  "receituario-antimicrobiano": "Receita de Antimicrobiano",
  "receituario-glp1": "Receita com Retenção — Análogo de GLP-1",
  exames: "Pedido de Exames",
  texto: "Documento médico",
};

interface Metadados {
  valido: boolean;
  // B292/AI_133 — link expira 90 dias após a assinatura. Quando true, a página
  // mostra a autenticidade mas não pede o 2º fator (orienta pedir reenvio).
  expirado?: boolean;
  medicoNome: string;
  registro: string;
  tipo: string;
  assinadoEm: string;
  // B292 — qual 2º fator pedir: 4 últimos dígitos do celular (documentos novos) ou
  // data de nascimento (documentos do B290 original). Ausente → "nascimento".
  fator?: "nascimento" | "celular4";
}

function formatarDataHora(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(
    d.getHours()
  )}:${p(d.getMinutes())}`;
}

const card: React.CSSProperties = {
  background: "white",
  borderRadius: "14px",
  boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
  padding: "20px 22px",
  marginBottom: "16px",
};

export default function ValidacaoPrescricao() {
  const { codigo: codigoUrl } = useParams<{ codigo: string }>();
  const navigate = useNavigate();
  const codigo = normalizarCodigo(codigoUrl || "");

  const [estado, setEstado] = useState<
    "carregando" | "entrada" | "ok" | "expirado" | "naoEncontrado" | "erro"
  >("carregando");
  const [meta, setMeta] = useState<Metadados | null>(null);
  // Quando a URL é /validar (sem código): campo para digitar/colar o código.
  const [codigoDigitado, setCodigoDigitado] = useState("");

  // 2ª conferência (2º fator) → revela o documento. `valor` = 4 dígitos do celular
  // (B292) ou data de nascimento (B290), conforme `meta.fator`.
  const [valor, setValor] = useState("");
  const [verificando, setVerificando] = useState(false);
  const [erro2f, setErro2f] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");

  // B292 — o campo segue o fator que o SERVIDOR informa no GET (um só por doc; desde
  // o B294-FU4/AI_150 ele é derivado do celular do cadastro na hora, não do que ficou
  // gravado na assinatura — corrigir o cadastro conserta documentos já emitidos). NÃO mostramos
  // os dois campos "aceitando qualquer um": isso reabriria a data de nascimento (o
  // fator fraco que o B292 existe para eliminar — risco caso Barueri/TJSP). Como todo
  // paciente tem celular, o ramo "nascimento" é só fallback de cadastro legado e
  // praticamente não ocorre; a distinção visível de fator é, na prática, resíduo nulo.
  const ehCelular = meta?.fator === "celular4";

  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        if (!codigo) {
          // URL /validar sem código → mostra o campo para digitar/colar o código.
          if (ativo) setEstado("entrada");
          return;
        }
        const r = await fetch(
          `/api/validar?codigo=${encodeURIComponent(codigo)}`
        );
        if (!ativo) return;
        if (r.status === 404) {
          setEstado("naoEncontrado");
          return;
        }
        if (!r.ok) {
          setEstado("erro");
          return;
        }
        const data = (await r.json()) as Metadados;
        if (!ativo) return;
        setMeta(data);
        // B292/AI_133: link de 90d expirado → mostra autenticidade, mas não o 2º fator.
        setEstado(data.expirado ? "expirado" : "ok");
      } catch {
        if (ativo) setEstado("erro");
      }
    })();
    return () => {
      ativo = false;
    };
  }, [codigo]);

  // B302: pdfUrl agora é uma URL https (não blob). Mantém o revoke guardado só
  // para o caso de um blob legado, sem efeito colateral em URL comum.
  useEffect(() => {
    return () => {
      if (pdfUrl && pdfUrl.startsWith("blob:")) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  const irParaCodigo = (e: React.FormEvent) => {
    e.preventDefault();
    const c = normalizarCodigo(codigoDigitado);
    if (c) navigate(`/validar/${c}`);
  };

  const revelarDocumento = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro2f("");
    setVerificando(true);
    try {
      const r = await fetch("/api/validar", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ codigo, valor }),
      });
      if (r.status === 403) {
        setErro2f(
          ehCelular
            ? "Os 4 dígitos não conferem. Confira e tente de novo."
            : "Data de nascimento não confere. Confira e tente de novo."
        );
        return;
      }
      if (r.status === 429) {
        setErro2f("Muitas tentativas. Aguarde alguns minutos e tente de novo.");
        return;
      }
      if (r.status === 410) {
        // Link expirou (90d) — defesa caso a página esteja em cache/stale.
        setEstado("expirado");
        return;
      }
      if (!r.ok) {
        setErro2f("Não foi possível abrir o documento agora. Tente mais tarde.");
        return;
      }
      const data = (await r.json()) as { viewToken: string };
      // B302: em vez de um blob local (que o iOS não renderiza em <iframe> nem abre
      // em nova aba), aponta para a URL https do backend, que serve o PDF inline com
      // o token de visualização de vida curta. Renderiza nativamente no iPhone.
      setPdfUrl(
        `/api/validar?codigo=${encodeURIComponent(
          codigo
        )}&view=${encodeURIComponent(data.viewToken)}`
      );
    } catch {
      setErro2f("Não foi possível abrir o documento agora. Tente mais tarde.");
    } finally {
      setVerificando(false);
    }
  };

  const rotuloTipo = (t: string) => ROTULO_TIPO[t] || "Documento médico";

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)",
        padding: "24px 14px",
        fontFamily: "'Helvetica Neue', 'Segoe UI', Arial, sans-serif",
      }}
    >
      <div style={{ maxWidth: "560px", margin: "0 auto" }}>
        {/* CABEÇALHO */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: "18px",
          }}
        >
          <img
            src={logoBase64}
            alt="Logo"
            style={{
              width: "52px",
              height: "52px",
              objectFit: "contain",
              borderRadius: "50%",
              background: "white",
              padding: "4px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            }}
          />
          <div>
            <div style={{ color: "white", fontSize: "20px", fontWeight: 700 }}>
              Validação de documento
            </div>
            <div style={{ color: "rgba(255,255,255,0.85)", fontSize: "13px" }}>
              hepato.app · prescrição digital
            </div>
          </div>
        </div>

        {estado === "carregando" && (
          <div style={card}>
            <p style={{ margin: 0, color: "#333" }}>Verificando o documento…</p>
          </div>
        )}

        {estado === "entrada" && (
          <div style={card}>
            <div style={{ fontSize: "16px", fontWeight: 700, color: "#222" }}>
              Validar um documento
            </div>
            <p style={{ fontSize: "13.5px", color: "#555", lineHeight: 1.6 }}>
              Escaneie o QR Code do documento ou digite o código de validação
              impresso no rodapé (ex.: <strong>XXXX-XXXX-XXXX</strong>).
            </p>
            <form onSubmit={irParaCodigo}>
              <input
                type="text"
                value={codigoDigitado}
                onChange={(e) => setCodigoDigitado(e.target.value)}
                placeholder="XXXX-XXXX-XXXX"
                autoCapitalize="characters"
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: "12px",
                  fontSize: "16px",
                  letterSpacing: "1px",
                  textAlign: "center",
                  border: "1px solid #ccc",
                  borderRadius: "10px",
                  marginBottom: "10px",
                }}
              />
              <button
                type="submit"
                disabled={!normalizarCodigo(codigoDigitado)}
                style={{
                  width: "100%",
                  padding: "13px",
                  fontSize: "16px",
                  fontWeight: 700,
                  color: "white",
                  background: normalizarCodigo(codigoDigitado)
                    ? COR_AZUL
                    : "#94a3b8",
                  border: "none",
                  borderRadius: "10px",
                  cursor: normalizarCodigo(codigoDigitado) ? "pointer" : "default",
                }}
              >
                Validar
              </button>
            </form>
          </div>
        )}

        {estado === "naoEncontrado" && (
          <div style={card}>
            <div
              style={{ fontSize: "17px", fontWeight: 700, color: "#b91c1c" }}
            >
              ✕ Documento não encontrado
            </div>
            <p style={{ fontSize: "14px", color: "#444", lineHeight: 1.6 }}>
              O código <strong>{formatarCodigoExibicao(codigo)}</strong> não
              corresponde a nenhum documento assinado. Confira se digitou
              corretamente ou escaneie o QR Code do documento novamente.
            </p>
          </div>
        )}

        {estado === "expirado" && (
          <div style={card}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                background: "#fef3c7",
                color: "#92400e",
                borderRadius: "999px",
                padding: "6px 14px",
                fontWeight: 700,
                fontSize: "15px",
                marginBottom: "14px",
              }}
            >
              ⏳ Link expirado
            </div>
            <p style={{ fontSize: "14px", color: "#444", lineHeight: 1.6 }}>
              Este link de visualização expirou (validade de 90 dias). O documento
              continua válido — peça ao médico para <strong>reenviar</strong> o link.
            </p>
            {meta && (
              <div style={{ marginTop: "8px" }}>
                <Linha rotulo="Assinado por" valor={meta.medicoNome} />
                <Linha rotulo="Registro profissional" valor={meta.registro} />
                <Linha rotulo="Tipo de documento" valor={rotuloTipo(meta.tipo)} />
                <Linha
                  rotulo="Data da assinatura"
                  valor={formatarDataHora(meta.assinadoEm)}
                />
              </div>
            )}
          </div>
        )}

        {estado === "erro" && (
          <div style={card}>
            <p style={{ margin: 0, color: "#444" }}>
              Não foi possível verificar agora. Tente novamente em instantes.
            </p>
          </div>
        )}

        {estado === "ok" && meta && (
          <>
            {/* AUTENTICIDADE */}
            <div style={card}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  background: "#eaf5ea",
                  color: COR_VERDE,
                  borderRadius: "999px",
                  padding: "6px 14px",
                  fontWeight: 700,
                  fontSize: "15px",
                  marginBottom: "14px",
                }}
              >
                ✓ Documento autêntico
              </div>

              <Linha rotulo="Assinado por" valor={meta.medicoNome} />
              <Linha rotulo="Registro profissional" valor={meta.registro} />
              <Linha rotulo="Tipo de documento" valor={rotuloTipo(meta.tipo)} />
              <Linha
                rotulo="Data da assinatura"
                valor={formatarDataHora(meta.assinadoEm)}
              />
              <Linha rotulo="Código" valor={formatarCodigoExibicao(codigo)} />

              <p
                style={{
                  fontSize: "12.5px",
                  color: "#666",
                  lineHeight: 1.6,
                  marginTop: "14px",
                  marginBottom: 0,
                }}
              >
                Assinatura eletrônica qualificada ICP-Brasil (MP 2.200-2/2001 e
                Lei 14.063/2020). A validação legal da assinatura também pode ser
                feita no portal oficial{" "}
                <a
                  href="https://validar.iti.gov.br"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: COR_AZUL }}
                >
                  validar.iti.gov.br
                </a>
                .
              </p>
            </div>

            {/* 2ª CONFERÊNCIA → DOCUMENTO */}
            {!pdfUrl && (
              <div style={card}>
                <div
                  style={{ fontSize: "16px", fontWeight: 700, color: "#222" }}
                >
                  Ver o documento
                </div>
                <p
                  style={{
                    fontSize: "13.5px",
                    color: "#555",
                    lineHeight: 1.6,
                  }}
                >
                  Para proteger os dados de saúde, informe{" "}
                  {ehCelular ? (
                    <>
                      os <strong>4 últimos dígitos do celular do paciente</strong>
                    </>
                  ) : (
                    <>
                      a <strong>data de nascimento do paciente</strong>
                    </>
                  )}{" "}
                  para abrir o documento.
                </p>
                <form onSubmit={revelarDocumento}>
                  {ehCelular ? (
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      value={valor}
                      onChange={(e) =>
                        setValor(e.target.value.replace(/\D/g, "").slice(0, 4))
                      }
                      required
                      placeholder="0000"
                      maxLength={4}
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        padding: "12px",
                        fontSize: "20px",
                        letterSpacing: "8px",
                        textAlign: "center",
                        border: "1px solid #ccc",
                        borderRadius: "10px",
                        marginBottom: "10px",
                      }}
                    />
                  ) : (
                    <input
                      type="date"
                      value={valor}
                      onChange={(e) => setValor(e.target.value)}
                      required
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        padding: "12px",
                        fontSize: "16px",
                        border: "1px solid #ccc",
                        borderRadius: "10px",
                        marginBottom: "10px",
                      }}
                    />
                  )}
                  {erro2f && (
                    <div
                      style={{
                        color: "#b91c1c",
                        fontSize: "13.5px",
                        marginBottom: "10px",
                      }}
                    >
                      {erro2f}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={
                      verificando || (ehCelular ? valor.length !== 4 : !valor)
                    }
                    style={{
                      width: "100%",
                      padding: "13px",
                      fontSize: "16px",
                      fontWeight: 700,
                      color: "white",
                      background: verificando ? "#94a3b8" : COR_AZUL,
                      border: "none",
                      borderRadius: "10px",
                      cursor: verificando ? "default" : "pointer",
                    }}
                  >
                    {verificando ? "Verificando…" : "Abrir documento"}
                  </button>
                </form>
              </div>
            )}

            {pdfUrl && (
              <div style={card}>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "10px",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "12px",
                  }}
                >
                  <div style={{ fontSize: "16px", fontWeight: 700 }}>
                    Documento assinado
                  </div>
                  <a
                    href={pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: "8px 14px",
                      background: COR_AZUL,
                      color: "white",
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontWeight: 600,
                      textDecoration: "none",
                    }}
                  >
                    Abrir em tela cheia
                  </a>
                </div>
                <p
                  style={{
                    margin: "0 0 10px",
                    fontSize: "12px",
                    color: "#666",
                  }}
                >
                  No celular, se o documento não aparecer abaixo, toque em{" "}
                  <strong>Abrir em tela cheia</strong>.
                </p>
                <iframe
                  title="Documento assinado"
                  src={pdfUrl}
                  style={{
                    width: "100%",
                    height: "70vh",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                  }}
                />
              </div>
            )}
          </>
        )}

        <div
          style={{
            textAlign: "center",
            color: "rgba(255,255,255,0.8)",
            fontSize: "12px",
            marginTop: "8px",
          }}
        >
          hepato.app — Hepatologia e Transplante Hepático
        </div>
      </div>
    </div>
  );
}

function Linha({ rotulo, valor }: { rotulo: string; valor: string }) {
  if (!valor) return null;
  return (
    <div style={{ marginBottom: "8px" }}>
      <div style={{ fontSize: "12px", color: "#888" }}>{rotulo}</div>
      <div style={{ fontSize: "15px", color: "#222", fontWeight: 600 }}>
        {valor}
      </div>
    </div>
  );
}
